// src/store/dashboardSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// Edge Device service imports removed - focusing on IQWatch devices only
import {
  getIQWatchDevicesBySite,
  getIQWatchDevicesList,
  getAllIQWatchDevices,
} from "../services/iqwatchService.ts";
import { IQWatchDeviceWithData, IQWatchDevice } from "../types";

export type DeviceFilter = "all" | "online" | "offline" | "low_battery";

interface DashboardState {
  iqwatchDevices: IQWatchDeviceWithData[];
  iqwatchPagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  } | null;
  iqwatchSummary: {
    total: number;
    online: number;
    offline: number;
    low_battery: number;
  } | null;
  sortField: string | null;
  sortOrder: "asc" | "desc" | null;
  deviceFilter: DeviceFilter;
  searchTerm: string;
  loading: {
    iqwatchDevices: boolean;
  };
  error: string | null;
  lastFetchTime: number;
}

// Helper function to convert IQWatchDevice to IQWatchDeviceWithData
const convertDeviceToDeviceWithData = (
  device: IQWatchDevice
): IQWatchDeviceWithData => {
  return {
    device,
    latest_data: {
      timestamp: device.last_data_timestamp || device.last_sync_at,
      status: device.status,
      soc: parseFloat(device.last_soc) || 0,
      battery_voltage: parseFloat(device.last_battery_voltage) || 0,
      load_power: parseFloat(device.last_load_power) || 0,
      solar_power: parseFloat(device.last_solar_power) || 0,
      chipid: device.iqwatch_chip_id,
      mac: device.iqwatch_mac_address,
      data_source: "api",
      // Note: router_data will be undefined for organization-level queries
      // as the basic device list doesn't include this detailed information
      router_data: undefined,
    },
  };
};

// fetchDevicesData removed - focusing on IQWatch devices only

// fetchDeviceDetails removed - focusing on IQWatch devices only

// 异步thunk用于获取iQWatch设备 (添加分页、排序、搜索和过滤支持)
export const fetchIQWatchDevices = createAsyncThunk(
  "dashboard/fetchIQWatchDevices",
  async (
    {
      siteId,
      orgId,
      page = 1,
      limit = 10,
      sortBy,
      sortOrder,
      search,
      filter,
    }: {
      siteId: string;
      orgId: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      search?: string;
      filter?: "all" | "online" | "offline" | "low_battery";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getIQWatchDevicesBySite(
        siteId,
        orgId,
        page,
        limit,
        sortBy,
        sortOrder,
        search,
        filter
      );
      return {
        devices: response.devices || [],
        pagination: response.pagination || null,
        summary: response.summary || null,
      };
    } catch (error: any) {
      // Extract more meaningful error message from axios error
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch devices";
      return rejectWithValue(errorMessage);
    }
  }
);

// 新增：异步thunk用于获取组织下的所有iQWatch设备 (添加排序、搜索和过滤支持)
export const fetchIQWatchDevicesByOrganization = createAsyncThunk(
  "dashboard/fetchIQWatchDevicesByOrganization",
  async (
    {
      orgId,
      page = 1,
      limit = 10,
      sortBy,
      sortOrder,
      search,
      filter,
    }: {
      orgId: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      search?: string;
      filter?: "all" | "online" | "offline" | "low_battery";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getIQWatchDevicesList(
        orgId,
        page,
        limit,
        sortBy,
        sortOrder,
        search,
        filter
      );

      // Convert IQWatchDevice[] to IQWatchDeviceWithData[]
      const devicesWithData = (response.devices || []).map(
        convertDeviceToDeviceWithData
      );

      return {
        devices: devicesWithData,
        pagination: response.pagination || null,
        summary: response.summary || null,
      };
    } catch (error: any) {
      // Extract more meaningful error message from axios error
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch devices";
      return rejectWithValue(errorMessage);
    }
  }
);

// 新增：异步thunk用于获取所有组织的iQWatch设备 (添加排序、搜索和过滤支持)
export const fetchAllIQWatchDevices = createAsyncThunk(
  "dashboard/fetchAllIQWatchDevices",
  async (
    {
      page = 1,
      limit = 10,
      sortBy,
      sortOrder,
      search,
      filter,
    }: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      search?: string;
      filter?: "all" | "online" | "offline" | "low_battery";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getAllIQWatchDevices(
        page,
        limit,
        sortBy,
        sortOrder,
        search,
        filter
      );

      // Convert IQWatchDevice[] to IQWatchDeviceWithData[]
      const devicesWithData = (response.devices || []).map(
        convertDeviceToDeviceWithData
      );

      return {
        devices: devicesWithData,
        pagination: response.pagination || null,
        summary: response.summary || null,
      };
    } catch (error: any) {
      // Extract more meaningful error message from axios error
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch devices";
      return rejectWithValue(errorMessage);
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    iqwatchDevices: [],
    iqwatchPagination: null,
    iqwatchSummary: null,
    sortField: null,
    sortOrder: null,
    deviceFilter: "all" as DeviceFilter,
    searchTerm: "",
    loading: {
      iqwatchDevices: false,
    },
    error: null,
    lastFetchTime: 0,
  } as DashboardState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDevicesData: (state) => {
      state.iqwatchDevices = [];
      state.iqwatchPagination = null;
      state.iqwatchSummary = null;
    },
    setDeviceFilter: (state, action: { payload: DeviceFilter }) => {
      state.deviceFilter = action.payload;
    },
    setSort: (
      state,
      action: {
        payload: { field: string | null; order: "asc" | "desc" | null };
      }
    ) => {
      state.sortField = action.payload.field;
      state.sortOrder = action.payload.order;
    },
    setSearchTerm: (state, action: { payload: string }) => {
      state.searchTerm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Edge Device related reducers removed - focusing on IQWatch devices only
      .addCase(fetchIQWatchDevices.pending, (state) => {
        state.loading.iqwatchDevices = true;
      })
      .addCase(fetchIQWatchDevices.fulfilled, (state, action) => {
        state.loading.iqwatchDevices = false;
        state.iqwatchDevices = action.payload.devices;
        state.iqwatchPagination = action.payload.pagination;
        state.iqwatchSummary = action.payload.summary;
      })
      .addCase(fetchIQWatchDevices.rejected, (state, action) => {
        state.loading.iqwatchDevices = false;
        state.error = action.payload as string;
      })
      // 新增：处理组织级设备获取
      .addCase(fetchIQWatchDevicesByOrganization.pending, (state) => {
        state.loading.iqwatchDevices = true;
      })
      .addCase(fetchIQWatchDevicesByOrganization.fulfilled, (state, action) => {
        state.loading.iqwatchDevices = false;
        state.iqwatchDevices = action.payload.devices;
        state.iqwatchPagination = action.payload.pagination;
        state.iqwatchSummary = action.payload.summary;
      })
      .addCase(fetchIQWatchDevicesByOrganization.rejected, (state, action) => {
        state.loading.iqwatchDevices = false;
        state.error = action.payload as string;
      })
      // 新增：处理所有组织的设备获取
      .addCase(fetchAllIQWatchDevices.pending, (state) => {
        state.loading.iqwatchDevices = true;
      })
      .addCase(fetchAllIQWatchDevices.fulfilled, (state, action) => {
        state.loading.iqwatchDevices = false;
        state.iqwatchDevices = action.payload.devices;
        state.iqwatchPagination = action.payload.pagination;
        state.iqwatchSummary = action.payload.summary;
      })
      .addCase(fetchAllIQWatchDevices.rejected, (state, action) => {
        state.loading.iqwatchDevices = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearDevicesData,
  setDeviceFilter,
  setSort,
  setSearchTerm,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
